/* eslint-disable @typescript-eslint/no-explicit-any */
import { BenefitCard } from "features/solutions/solutionsComponents/BenefitCard";
import React, { useMemo } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import { useItem } from "shared/dynamic-imports";
import { CustomRating } from "shared/ui-basics/ui-components/CustomRating";
import styled from "styled-components";
import { calculatesolutionType, getSolutionTypeData } from "utils/helpers";
import { SolutionType } from "utils/optionsSets/OptionSets";

type Props = {
	attribute: any;
	disabled: boolean;
	link: (id: string) => void;
};
const getLabelByKeyName = (key: string) => {
	switch (key) {
		case "impactCustomer":
			return "IMPACT_ON_CUSTOMER";
		case "customerBenefit":
			return "CUSTOMER_BENEFITS";
		case "implComplexity":
			return "COMPLEXITY_IMPL";
		case "implDuration":
			return "TIME_HORIZON";
		case "cost":
			return "IMPL_COST";
		case "revenueIncrease":
			return "REVENUE_INCREASE";
		case "impactBrand":
			return "IMPACT_ON_BRAND";
		case "engagementEffortRanking":
			return "ENGAGEMENT_EFFORT";
		default:
			return "N/A";
	}
};
const order = ["impactCustomer", "customerBenefit", "revenueIncrease", "impactBrand", "cost", "implComplexity", "engagementEffortRanking", "implDuration"];

export default function AdvancedRatings({ attribute, disabled }: Props): JSX.Element {
	const { currentItem, updateService, modifiedAttribNames, setModifiedAttribNames } = useItem();
	const { t } = useTranslation();
	const [show, setShow] = React.useState(false);
	const solutionType = currentItem.solutionType && currentItem.solutionType !== -1 ? currentItem.solutionType : calculatesolutionType(currentItem);
	const handleToggle = () => {
		const visible = !show;
		setShow(visible);
		if (!visible) {
			handleHide();
		}
	};
	const handleHide = () => {
		if (modifiedAttribNames.size > 0) {
			updateService({
				item: currentItem,
				modifiedAttribNames: Array.from(modifiedAttribNames.values()),
			});
			setModifiedAttribNames(new Set());
		}
	};
	return (
		<OverlayTrigger
			trigger="click"
			show={show}
			onToggle={handleToggle}
			onHide={handleHide as any}
			rootClose
			placement="bottom"
			overlay={
				<CustomPopover id="popover-contained">
					<PopoverComponent disabled={disabled} />
				</CustomPopover>
			}>
			<Placeholder>
				{solutionType && solutionType !== -1 ? (
					<BenefitCard classes="px-2" isActive={true} variant={getSolutionTypeData(solutionType)?.variant} text={getSolutionTypeData(solutionType)?.text} />
				) : (
					<span>{t("RATE_THIS_SOLUTION")}</span>
				)}
			</Placeholder>
		</OverlayTrigger>
	);
}
const PopoverComponent = (props: any) => {
	const { t } = useTranslation();
	const { currentItem, setCurrentItem, updateService } = useItem();
	const ratings = useMemo(
		() =>
			currentItem?.ratings
				? Object.entries(currentItem?.ratings)
						?.map(([key, value]: any) => ({ key, ...value, label: getLabelByKeyName(key) }))
						?.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key))
				: [],
		[currentItem.ratings]
	);
	const solutionType = currentItem.solutionType && currentItem.solutionType !== -1 ? currentItem.solutionType : calculatesolutionType(currentItem);

	const updateFunction = (item: any) => {
		const ratings = {
			...currentItem.ratings,
			[item.key]: { ...currentItem.ratings[item.key], rating: item.rating },
		};
		const solutionType = calculatesolutionType({ ...currentItem, ratings });
		setCurrentItem({ ...currentItem, ratings, solutionType });
		updateService({ item: { ...currentItem, ratings, solutionType }, modifiedAttribNames: [`ratings.${item.key}.rating`, "solutiontype"] });
	};

	return (
		<>
			<Title>{t("RATINGS")}</Title>
			<Row className="p-2">
				<Col className="d-flex flex-column gap-4">
					{ratings?.slice(0, 4)?.map((rating: any) => (
						<RatingWrapper key={rating.key}>
							<Labels>{t(rating.label)}</Labels>
							<CustomRating object={rating} setObject={updateFunction} attribute={"rating"} disabled={props.disabled} color={"#6A91F7"} />
						</RatingWrapper>
					))}
				</Col>
				<Col className="d-flex flex-column gap-4">
					{ratings?.slice(4, Infinity).map((rating: any) => (
						<RatingWrapper key={rating.key}>
							<Labels>{t(rating.label)}</Labels>
							<CustomRating object={rating} setObject={updateFunction} attribute={"rating"} disabled={props.disabled} color={"#F8AD48"} />
						</RatingWrapper>
					))}
				</Col>
			</Row>
			<FlexAlign>
				<Line />
				<RatingLabelsWrapper>
					<BenefitCard classes={"px-2"} isActive={SolutionType.QuickWin === solutionType} variant={"quickWin"} text={"QUICK_WIN"} />
					<BenefitCard classes={"px-2"} isActive={SolutionType.NiceToHave === solutionType} variant={"niceToHave"} text={"NICE_TO_HAVE"} />
					<BenefitCard classes={"px-2"} isActive={SolutionType.ComplexProject === solutionType} variant={"complexProj"} text={"COMPLEX_PROJ"} />
					<BenefitCard classes={"px-2"} isActive={SolutionType.ToAvoid === solutionType} variant={"toAvoid"} text={"TO_AVOID"} />
				</RatingLabelsWrapper>
			</FlexAlign>
		</>
	);
};
const CustomPopover = styled(Popover)`
	max-width: 750px;
	width: 100%;
	padding: 8px;
	gap: 4px;
	display: flex;
	flex-direction: column;
	box-shadow: 1px 8px 16px 0px rgba(0, 0, 0, 0.15);

	button {
		justify-content: start;
	}
	.popover-arrow {
		display: none;
	}
`;
interface WrapperProps extends React.HTMLAttributes<HTMLDivElement> {
	key?: string;
}
const RatingWrapper = styled.div<WrapperProps>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 8px;
	background-color: #fafafa;
	border-radius: 4px;
`;
const Title = styled.h3`
	color: var(--color-Grey-Primary-Text, #262626);
	font-family: Inter;
	font-size: 20px;
	font-style: normal;
	font-weight: 400;
	line-height: 20px;
	padding: 8px; /* 100% */
`;
const Placeholder = styled.div`
	display: flex;
	align-items: center;
	height: 100%;
	padding-left: 8px;
	cursor: pointer;
	:hover {
		background-color: #efefef;
	}
`;
const FlexAlign = styled.div`
	display: flex;
	align-items: center;
	position: relative;
	justify-content: center;
	min-height: 50px;
`;

const Line = styled.div`
	background: var(--color-Grey-Background, #f5f5f5);
	height: 8px;
	width: 679px;
	position: absolute;
	z-index: 1;
`;
const Labels = styled.div`
	color: #333333;
	font-family: "Inter";
	font-size: 14px;
	font-weight: 400;
	line-height: 20px;
`;

const RatingLabelsWrapper = styled.div`
	position: absolute;
	z-index: 2;
	display: flex;
	justify-content: space-around;
	align-items: center;
	width: 600px;
`;
